/** Sessions as the coach app gathers them (TeamSessions, CoachSessions). */
import { byName, keyOf, liftBlocks, liftKeys, norm, plural, skippedWorkout, working } from "./plan.js";
import { isBetter, recordText, setText, topSet } from "./sets.js";
import { mine } from "./text.js";
import type { Block, Exercise, Plan, PlanRow, PublishedAthlete, Session, SetLog, Workout } from "./types.js";

export interface Published {
  week: string;
  columns: string[];
  sessions: PlanRow[];
  athletes: PublishedAthlete[];
  document: unknown;
}

export interface CoachSession {
  id: string;
  day: string;
  lifts: string[];
  workouts: Workout[];
  teamSessionID: string | null;
  plan: Plan | null;
  given: PublishedAthlete[];
  sheet: { week: string } | null;
}

export interface WorkoutGroup {
  day: string;
  members: number[];
  lifts: string[];
}

export function groupWorkouts(list: { day: string; session: Session }[]): WorkoutGroup[] {
  const lifts = list.map((w) => liftKeys(w.session.blocks));
  const order = list
    .map((_, i) => i)
    .filter((i) => lifts[i]!.length > 0)
    .sort((a, b) => (lifts[a]!.length !== lifts[b]!.length ? lifts[b]!.length - lifts[a]!.length : a - b));
  const groups: WorkoutGroup[] = [];
  for (const index of order) {
    const mineSet = new Set(lifts[index]);
    let best: { at: number; shared: number } | null = null;
    for (let gi = 0; gi < groups.length; gi++) {
      const g = groups[gi]!;
      if (g.day !== list[index]!.day) continue;
      const shared = g.lifts.filter((k) => mineSet.has(k)).length;
      if (shared > 0 && shared * 2 >= Math.min(mineSet.size, g.lifts.length) && (!best || shared > best.shared)) best = { at: gi, shared };
    }
    if (best) {
      const g = groups[best.at]!;
      g.members.push(index);
      for (const k of lifts[index]!) if (!g.lifts.includes(k)) g.lifts.push(k);
    } else {
      groups.push({ day: list[index]!.day, members: [index], lifts: lifts[index]!.slice() });
    }
  }
  return groups
    .map((g, i) => ({ g, i }))
    .sort((a, b) => {
      if (a.g.day !== b.g.day) return a.g.day > b.g.day ? -1 : 1;
      if (a.g.members.length !== b.g.members.length) return b.g.members.length - a.g.members.length;
      return a.i - b.i;
    })
    .map((x) => x.g);
}

/**
 * The rows a published week gave an athlete. A week the builder published with targets says so
 * outright (`assigned`), matched exactly; any other week is matched by name as the phone does.
 */
export function rowsGiven(weeks: Published[], athleteID: string, groups: string[]): PlanRow[] {
  const out: PlanRow[] = [];
  const byName: Published[] = [];
  for (const w of weeks) {
    const a = w.athletes.find((x) => x.id === athleteID);
    if (a && a.assigned !== undefined) {
      if (a.assigned !== null) out.push(...w.sessions.filter((r) => r.group === a.assigned));
    } else byName.push(w);
  }
  if (byName.length) {
    const columns: { [week: string]: string[] } = {};
    for (const w of byName) columns[w.week] = w.columns ?? [];
    out.push(...mine(byName.flatMap((w) => w.sessions), columns, groups).sessions);
  }
  return out;
}

export function coachSessions(workouts: Workout[], weeks: Published[], days?: [string, string]): CoachSession[] {
  const sessions: CoachSession[] = [];
  const claimed = new Set<Workout>();
  let rows: PlanRow[] = [];
  const columns: { [week: string]: string[] } = {};
  const sheets: { [week: string]: unknown } = {};
  const athletes: PublishedAthlete[] = [];
  for (const w of weeks) {
    rows = rows.concat(w.sessions);
    const key = w.sessions[0]?.week;
    if (key) {
      columns[key] = w.columns ?? [];
      sheets[key] = w.document ?? null;
    }
    for (const a of w.athletes ?? []) if (!athletes.some((x) => x.id === a.id)) athletes.push(a);
  }
  if (rows.length) {
    const given = new Map<string, Set<string>>();
    for (const a of athletes) given.set(a.id, new Set(rowsGiven(weeks, a.id, a.groups ?? []).map((r) => norm(r.sessionID))));
    const seen = new Set<string>();
    for (const row of rows) {
      const id = norm(row.sessionID);
      if (seen.has(id)) continue;
      seen.add(id);
      if (!liftBlocks(row.plan?.blocks).length) continue;
      if (days && (row.date < days[0] || row.date > days[1])) continue;
      const theirs = workouts.filter((w) => norm(w.session.teamSessionID) === id);
      for (const w of theirs) claimed.add(w);
      const lifts = liftKeys(row.plan.blocks);
      for (const w of theirs) for (const k of liftKeys(w.session.blocks)) if (!lifts.includes(k)) lifts.push(k);
      sessions.push({
        id,
        day: row.date,
        lifts,
        workouts: theirs.slice().sort((a, b) => liftKeys(b.session.blocks).length - liftKeys(a.session.blocks).length),
        teamSessionID: id,
        plan: row.plan,
        given: athletes.filter((a) => given.get(a.id)?.has(id)),
        sheet: row.week && sheets[row.week] ? { week: row.week } : null,
      });
    }
  }
  const rest = workouts.filter((w) => !claimed.has(w));
  const matched = groupWorkouts(rest.map((w) => ({ day: w.date, session: w.session }))).map((g): CoachSession => {
    const members = g.members.map((i) => rest[i]!);
    return {
      id: members.map((w) => norm(w.session.id)).join(","),
      day: g.day,
      lifts: g.lifts,
      workouts: members,
      teamSessionID: null,
      plan: null,
      given: [],
      sheet: null,
    };
  });
  return sessions
    .concat(matched)
    .map((s, i) => ({ s, i }))
    .sort((a, b) => (a.s.day !== b.s.day ? (a.s.day > b.s.day ? -1 : 1) : a.i - b.i))
    .map((x) => x.s);
}

export function waiting(cs: CoachSession): PublishedAthlete[] {
  return cs.given.filter((a) => !cs.workouts.some((w) => w.athleteID === a.id));
}

export function sessionRecords(cs: CoachSession): number {
  return cs.workouts.reduce((n, w) => n + (w.records ?? []).length, 0);
}

/** Athletes whose every set of the session was skipped (§61.8): counted apart from done. */
export function skippedBy(cs: CoachSession): Set<string> {
  const did = new Set(cs.workouts.filter((w) => !skippedWorkout(w)).map((w) => w.athleteID));
  return new Set(cs.workouts.filter((w) => skippedWorkout(w) && !did.has(w.athleteID)).map((w) => w.athleteID));
}

export function athletesText(cs: CoachSession): string {
  if (cs.teamSessionID && cs.given.length) {
    const skipped = [...skippedBy(cs)].filter((id) => cs.given.some((a) => a.id === id)).length;
    return cs.given.length - waiting(cs).length - skipped + " of " + cs.given.length + " done" + (skipped ? " · " + skipped + " skipped" : "");
  }
  return plural(cs.workouts.length, "athlete", "athletes");
}

export function findExercise(cs: CoachSession, key: string): { exercise: Exercise; block: Block } | null {
  const lists = (cs.plan ? [cs.plan.blocks] : []).concat(cs.workouts.map((w) => w.session.blocks));
  for (const list of lists) {
    for (const block of liftBlocks(list)) {
      const exercise = (block.exercises ?? []).find((e) => keyOf(e) === key);
      if (exercise) return { exercise, block };
    }
  }
  return null;
}

export function liftName(cs: CoachSession, key: string): string {
  return findExercise(cs, key)?.exercise.name ?? key;
}

export interface LiftLine {
  athleteID: string;
  name: string;
  workout: Workout | null;
  top: SetLog | null;
  sets?: number;
  value: string | null;
  record: string | null;
  waiting?: boolean;
  /** Every set of the lift they logged was skipped (§61.8). */
  skipped?: boolean;
  past?: boolean;
  day?: string;
}

export function liftLines(cs: CoachSession, key: string, today: string): LiftLine[] {
  const lines: LiftLine[] = cs.workouts.map((w) => {
    const exercises: Exercise[] = [];
    for (const b of liftBlocks(w.session.blocks)) for (const e of b.exercises ?? []) if (keyOf(e) === key) exercises.push(e);
    const ids = new Set(exercises.map((e) => norm(e.id)));
    const logs = (w.session.logs ?? []).filter((l) => ids.has(norm(l.exerciseID)));
    const top = topSet(logs);
    const record = top ? (w.records ?? []).find((r) => norm(r.logID) === norm(top.id)) : undefined;
    return {
      athleteID: w.athleteID,
      name: w.name,
      workout: w,
      top,
      sets: logs.filter((l) => !l.skipped).length,
      skipped: !top && logs.length > 0 && logs.every((l) => l.skipped),
      value: top ? setText(top, exercises[0]) : null,
      record: record ? recordText(record.label, top!, exercises[0]) : null,
    };
  });
  lines.sort((a, b) => {
    if (!a.top && !b.top) return Number(!!a.skipped) - Number(!!b.skipped) || byName(a, b);
    if (!a.top) return 1;
    if (!b.top) return -1;
    if (isBetter(a.top, b.top)) return -1;
    if (isBetter(b.top, a.top)) return 1;
    return byName(a, b);
  });
  const past = cs.day < today;
  return lines.concat(
    waiting(cs)
      .slice()
      .sort(byName)
      .map((a) => ({ athleteID: a.id, name: a.name, workout: null, top: null, value: null, record: null, waiting: true, past, day: cs.day })),
  );
}
